import React, { CSSProperties } from 'react';

import SignUpBanner from '../components/Banners/SignUp';
import SignUpBannerMini from '../components/Banners/SignUpMini';
import { Layout } from '../components/common';
import { MetaData } from '../components/common/meta';
import { FinancialTools } from '../components/FinancialTools/FinancialTools';
import { PostAuthor } from '../components/Post/Author';
import { PostAuthorDescription } from '../components/Post/Author/Description';
import { IPostCard } from '../components/Post/Card';
import { PopularPosts } from '../components/Post/Popular';
import { PostSocialLinks } from '../components/Post/SocialLinks';
import ProgressBarDesktop from '../components/ProgressBar/ProgressBarDesktop';
import RenderOnVisible from '../components/RenderOnVisible';
import { TableOfContents } from '../components/TableOfContents/TableOfContents';
import {
  renderGhostAuthorAside,
  renderGhostChatBubbles,
  renderGhostImageFloat,
  renderGhostInlineTOC,
} from '../utils/ghostSnippets';
import { GhostRelatedArticlesCarousel } from '../utils/GhostSnippets/RelatedArticlesCarousel';
import { GhostSuggestedElements } from '../utils/GhostSnippets/SuggestedElements';
import { optimizeContent } from '../utils/helpers';
import { lazyLoading } from '../utils/lazyLoading';
import trustAllScripts from '../utils/trustAllScripts';

import stylePost from '../styles/pages/post.scss';
import style from '../styles/pages/post.scss';

import { Tags } from '@tryghost/helpers-gatsby';
import cn from 'classnames';
import { graphql, Link } from 'gatsby';
import { Helmet } from 'react-helmet';
import { Sticky, StickyContainer } from 'react-sticky';

interface IPostProps {
  data: {
    ghostPost: IPostCard;
    allGhostPost: { edges: { node: IPostCard }[] };
    allGhostAuthor: {
      edges: {
        node: {
          name: string;
          slug: string;
          profile_image: string;
          bio: string;
        };
      }[];
    };
    allAuthorsBioFullJson: { edges: { node: { slug: string; bio: string } }[] };
    // allGhostPostFeatured: any;
    // allGhostPageFeatured: any;
    article1: any;
    article2: any;
    article3: any;
  };
  location: any;
}

/**
 * Single post view (/:slug)
 *
 * This file renders a single post and loads all the content.
 *
 */

// RenderOnVisible since content not visible on most screens
// Extracted since onRouteUpdate from gatsby-browser.js runs before mounted
const Content = ({
  content,
  allGhostAuthor,
}: {
  content: string;
  allGhostAuthor: {
    edges: {
      node: {
        name: string;
        slug: string;
        profile_image: string;
        bio: string;
      };
    }[];
  };
}) => {
  React.useEffect(() => {
    trustAllScripts();

    renderGhostImageFloat();

    renderGhostInlineTOC();

    renderGhostChatBubbles(allGhostAuthor.edges);

    renderGhostAuthorAside(allGhostAuthor.edges);

    lazyLoading();
  }, []);

  if (!content) {
    return null;
  }

  return (
    <section
      className={cn(
        'load-external-scripts',
        stylePost.singleArticleFullContent,
        stylePost.singleArticleBodyContent
      )}
      dangerouslySetInnerHTML={{
        __html: optimizeContent(content),
      }}
    />
  );
};

const Post = React.memo(({ data, location }: IPostProps) => {
  const {
    allGhostAuthor,
    allAuthorsBioFullJson,
    ghostPost,
    article1,
    article2,
    article3,
    // allGhostPostFeatured,
    // allGhostPageFeatured,
  } = data;

  const {
    html = '',
    tags,
    primary_author,
    codeinjection_styles,
    title,
    custom_excerpt,
    feature_image,
    updated_at,
  } = ghostPost;

  React.useEffect(() => {
    tags.push(
      tags.splice(
        tags.findIndex((tag) => tag && tag.slug === 'guest-posts'),
        1
      )[0]
    );

    // Note, some authors have no full bio.
    const authorsBioFull = allAuthorsBioFullJson.edges.find(({ node }) => {
      return node.slug === primary_author.slug;
    });

    if (authorsBioFull) {
      primary_author.bio = authorsBioFull.node.bio;
    }
  }, []);

  const ghostDisclosure =
    typeof window !== 'undefined' &&
    document &&
    document.getElementById('ghostAdditionalDisclosure')?.innerHTML;

  return (
    <>
      <MetaData data={data} location={location} />
      <Helmet>
        <style type="text/css">{`${codeinjection_styles}`}</style>
      </Helmet>
      <Layout postTitle={title} mainClass={style.main} isArticle>
        <StickyContainer className={stylePost.sticky}>
          <Sticky>
            {({ style, isSticky }: { style: CSSProperties; isSticky: boolean }) => (
              <div style={{ ...style }}>
                <RenderOnVisible>
                  <ProgressBarDesktop
                    className={isSticky ? stylePost.stickyPrograssBar : ''}
                    title={title}
                    content={html}
                  />
                </RenderOnVisible>
              </div>
            )}
          </Sticky>
          <div className="container">
            <div className="row">
              <article className={cn('col-12 col-lg-8', stylePost.singleArticle)}>
                <section className={stylePost.singleArticleFullContent}>
                  {tags && (
                    <Tags
                      classes={cn(stylePost.singleArticleTags, style.featuredTag)}
                      post={ghostPost}
                      visibility="public"
                      limit={1}
                      permalink="/tag/:slug/"
                    />
                  )}
                  <h1 className={stylePost.singleArticleTitle}>{title}</h1>
                  {custom_excerpt ? (
                    <p className={stylePost.singleArticleExcerpt}>{custom_excerpt}</p>
                  ) : null}
                  <div className="d-md-flex justify-content-between">
                    <PostAuthor
                      publishedAt={updated_at}
                      primary_author={primary_author}
                      post={ghostPost}
                    />
                    <div className={cn('d-none d-md-block', style.singleArticleAuthorLinks)}>
                      <PostSocialLinks feature_image={feature_image} />
                    </div>
                  </div>
                  <p className={style.disclaimerText}>
                    This post may contain affiliate links and/or paid placement.{' '}
                    <Link to="/disclosure/">Click here to read our full disclosure.</Link>{' '}
                    {`${ghostDisclosure || ''}`}
                  </p>
                </section>
                {feature_image ? (
                  <figure className={stylePost.singleArticleFeatureImage}>
                    <img className="lazy" height="220px" data-src={feature_image} alt={title} />
                    <div className={cn('d-md-none', style.singleArticleAuthorLinks)}>
                      <PostSocialLinks feature_image={feature_image} />
                    </div>
                  </figure>
                ) : null}
                <TableOfContents title={title} content={html} />
                {/* The main post content */}
                <Content content={html} allGhostAuthor={allGhostAuthor} />
                <section className={stylePost.singleArticleFullContent}>
                  <PostSocialLinks feature_image={feature_image} />
                  {tags && (
                    <div className={stylePost.singleArticleFooterTags}>
                      <Tags
                        classes={stylePost.singleArticleTags}
                        post={ghostPost}
                        visibility="public"
                        separator=""
                        permalink="/tag/:slug/"
                      />
                    </div>
                  )}
                </section>
                <PostAuthorDescription primary_author={primary_author} />
                <GhostRelatedArticlesCarousel ghostPost={ghostPost} />
                <GhostSuggestedElements ghostPost={ghostPost} />
                <section className={stylePost.commentsContainer}>
                  <Helmet>
                    <script defer src={process.env.COMMENTO_SCRIPT_SRC} data-no-fonts="true" />
                  </Helmet>
                  <h3 className={stylePost.commentsHeading}>Comments</h3>
                  <div id="commento" />
                </section>
              </article>
              <section className={cn('d-none d-lg-block col-lg-4 pr-1')}>
                {/* <Sticky>
                  {({ style: stickyStyle }: { style: CSSProperties; isSticky: boolean }) => (
                    <div style={{ ...stickyStyle }}> */}
                <div className={style.sidebar}>
                  <SignUpBannerMini />
                  <h3>More Reading</h3>
                  <PopularPosts
                    posts={article1?.edges?.concat(article2?.edges).concat(article3?.edges) || []}
                  />
                  <FinancialTools />
                  <div id="ghostMiscSideBarContent" />
                </div>
                {/* </div>
                  )}
                </Sticky> */}
              </section>
            </div>
          </div>
        </StickyContainer>
        <SignUpBanner />
        <style
          dangerouslySetInnerHTML={{
            __html: `
/*Mediavine Mobile Fix*/
@media only screen and (max-width: 425px) {
  article.col-12.col-lg-8.m-auto {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
  .page--page--30zjZ > .container {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
  .page--page--30zjZ {
    padding-left: 37px !important;
    padding-right: 37px !important;
  }
}
@media only screen and (max-width: 359px) {
  article.col-12.col-lg-8.post--singleArticle--4N84x {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
  article.col-12.col-lg-8.m-auto {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
  .page--page--30zjZ > .container {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
  .page--page--30zjZ {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
}
/*Back to Top CSS*/
body.mediavine-videohas-sticky #pe-widget-bell {
  bottom: 300px !important;
}
body.adhesion:not(.mediavine-videohas-sticky) #pe-widget-bell {
  bottom: 120px !important;
}
`,
          }}
        ></style>
      </Layout>
    </>
  );
});

export default Post;

export const postQuery = graphql`
  query($slug: String!) {
    ghostPost(slug: { eq: $slug }) {
      ...GhostPostFields
    }
    allGhostAuthor {
      edges {
        node {
          name
          slug
          bio
          profile_image
        }
      }
    }
    allAuthorsBioFullJson {
      edges {
        node {
          bio
          slug
        }
      }
    }
    article1: allGhostPage(filter: { slug: { regex: "/^(build-wealth-guide)$/" } }, limit: 1) {
      edges {
        node {
          title
          slug
          feature_image
          primary_author {
            name
          }
        }
      }
    }
    article2: allGhostPost(filter: { slug: { regex: "/^(budget-happiness)$/" } }, limit: 1) {
      edges {
        node {
          title
          slug
          feature_image
          primary_author {
            name
          }
        }
      }
    }
    article3: allGhostPage(filter: { slug: { regex: "/^(side-hustles-make-money)$/" } }, limit: 1) {
      edges {
        node {
          title
          slug
          feature_image
          primary_author {
            name
          }
        }
      }
    }
    allGhostPageFeatured: allGhostPage(
      sort: { order: DESC, fields: [published_at, featured] }
      filter: { featured: { eq: true } }
      limit: 3
    ) {
      edges {
        node {
          slug
          feature_image
          featured
          title
          published_at
          updated_at
          primary_author {
            name
          }
          slug
        }
      }
    }
  }
`;
